import { useCallback, useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import {
  Button,
  Divider,
  Form,
  Header,
  Icon,
  Image,
  Input,
  Loader,
  Message,
  Modal,
  ModalActions,
  ModalContent,
} from "semantic-ui-react"

import actions from "actions"
import api from "api"
import { useAppDispatch, useAppSelector } from "store"
import constants from "utils/constants"
import util from "utils/utils"

import OrganisationChooser from "./OrganisationChooser"

function Login() {
  const [otp, setOtp] = useState("")
  const [otpModal, setOtpModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [securityPhrase, setSecurityPhrase] = useState("")
  const [showCustomChallenge, setShowCustomChallenge] = useState(false)
  const [showPasswordEntry, setShowPasswordEntry] = useState(false)
  const [proceedToLogin, setProceedToLogin] = useState(false)
  const [exchangeToken, setExchangeToken] = useState("")
  const [otherOrgs, setOtherOrgs] = useState([])
  const passwordRef = useRef()

  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const paramMessage = params.get("message")
  const ssoProtectionToken = params.get("ssoProtectionToken") || ""

  const dispatch = useAppDispatch()
  const onSelectOrganisation = useCallback(
    (org) => {
      dispatch(actions.organisations.receiveUrlOrganisation(org))
    },
    [dispatch],
  )
  const onUpdateEmail = useCallback(
    (e) => {
      dispatch(actions.entry.updateEmail(e))
    },
    [dispatch],
  )
  const onUpdatePassword = useCallback(
    (p) => {
      dispatch(actions.entry.updatePassword(p))
    },
    [dispatch],
  )
  const onUpdateChallengeResponse = useCallback(
    (r) => {
      dispatch(actions.entry.updateChallengeResponse(r))
    },
    [dispatch],
  )
  const onUpdateLoading = useCallback(
    (l) => {
      dispatch(actions.entry.updateLoading(l))
    },
    [dispatch],
  )

  const authErrorMessage = useAppSelector((state) => state.auth.errorMessage)
  const utmLink = useAppSelector((state) => state.auth.utmLink)
  const utmChallenge = useAppSelector((state) => state.auth.utmChallenge)
  const email = useAppSelector((state) => state.entry.email)
  const password = useAppSelector((state) => state.entry.password)
  const challengeResponse = useAppSelector((state) => state.entry.challengeResponse)
  const loading = useAppSelector((state) => state.entry.loading)

  const organisation = useAppSelector((state) => state.organisations.urlOrganisation)
  const shouldAutoLogin =
    organisation?.code && organisation?.auth?.types?.length === 1 && organisation?.auth?.types[0] !== "standard"

  useEffect(() => {
    if (shouldAutoLogin) {
      const authType = organisation.auth.types[0]
      const redirectAuthTypes = ["microsoft", "linkedin", "apple", "google"]
      if (redirectAuthTypes.includes(authType)) {
        util.loginWithOAuth(authType, organisation.code)
      } else if (authType === "saml") {
        util.loginWithSaml(organisation.code)
      }
    }
  }, [shouldAutoLogin, organisation?.code, organisation?.auth?.types])

  useEffect(() => {
    const exchangeTokenChecker = setInterval(() => {
      if (exchangeToken && document.visibilityState === "visible") {
        api.auth.loginWithToken(
          exchangeToken,
          () => {
            clearInterval(exchangeTokenChecker)
          },
          () => {},
        )
      }
    }, 2000)
    return function cleanup() {
      clearInterval(exchangeTokenChecker)
    }
  }, [exchangeToken])

  const authIsEnabled = (authMethod) => {
    // Special case for ssoProtection, which allows standard login when redirected from SSO protection check
    if (paramMessage === "ssoProtectionLoginRequired") {
      return authMethod === "standard"
    }
    return organisation?.auth?.types?.includes(authMethod)
  }

  const ssoIsEnabled = () => {
    // Disable all sso login methods when redirected from SSO protection check
    if (paramMessage === "ssoProtectionLoginRequired") {
      return false
    }
    const authMethods = organisation?.auth?.types || []
    return authMethods.length > 1 || (authMethods.length === 1 && authMethods[0] !== "standard")
  }

  const checkAccount = useCallback(
    (withEmail?: string) => {
      const emailToCheck = withEmail || email
      setErrorMessage("")
      setOtherOrgs([])
      if (!emailToCheck || !emailToCheck.match(constants.emailRegex)) return
      if (!organisation) {
        setShowPasswordEntry(true)
        setProceedToLogin(true)
        return
      }
      onUpdateLoading(true)
      api.auth.verifyAccount(
        organisation._id,
        emailToCheck,
        challengeResponse,
        (data) => {
          onUpdateLoading(false)
          if (data.exists) {
            setOtherOrgs(data.otherOrgs)
            if (data.challengePassed) {
              setShowPasswordEntry(true)
              setProceedToLogin(true)
              passwordRef.current?.focus()
            } else {
              setErrorMessage(data.challengeMessage)
              if (data.customChallengeRequired) {
                setShowCustomChallenge(true)
              }
            }
          } else if (data.challengePassed) {
            if (organisation.loginLinkEnabled) {
              setErrorMessage(
                `This email address is not registered with ${organisation.name}. Please check that it is correct or retry with another email.`,
              )
            } else {
              let then
              const path = location.pathname + location?.search
              if (path && path !== "/") then = path

              const linkUtm = params.get("utm-link")
              const challengeUtm = params.get("utm-challenge")
              const thenParam = params.get("then")
              if (thenParam && thenParam !== "/") then = thenParam
              navigate(
                `/register${then ? `?then=${encodeURIComponent(then)}` : ""}${challengeUtm ? `&utm-challenge=${challengeUtm}` : ""}${linkUtm ? `&utm-link=${linkUtm}` : ""}`,
              )
            }
          } else {
            setErrorMessage(data.challengeMessage)
            if (data.customChallengeRequired) {
              setShowCustomChallenge(true)
            }
          }
        },
        (err) => {
          setErrorMessage(err.message)
          onUpdateLoading(false)
        },
      )
    },
    [challengeResponse, email, organisation, onUpdateLoading, navigate, location, params],
  )

  useEffect(() => {
    const emailParam = params.get("email")
    if (emailParam && organisation) {
      params.delete("email")
      setParams(params)
      onUpdateEmail(emailParam)
      checkAccount(emailParam)
    }
  }, [onUpdateEmail, checkAccount, setParams, params, organisation])

  const login = () => {
    onUpdateLoading(true)
    setErrorMessage("")
    api.auth.login(
      {
        email,
        password,
        challengeResponse,
        exchangeToken,
        otp,
        org: organisation?._id,
        utmLink,
        utmChallenge,
        ssoProtectionToken,
      },
      (data) => {
        if (data.otpRequired) {
          setOtpModal(true)
          setExchangeToken(data.exchangeToken)
          onUpdateLoading(false)
        } else {
          onUpdateLoading(false)
          const then = params.get("then")
          if (then) {
            navigate(then)
          }
        }
      },
      (err) => {
        onUpdateLoading(false)
        setErrorMessage(err.message)
        setOtpModal(false)
        setOtp("")
      },
    )
  }

  const loginMagicLink = useCallback(() => {
    onUpdateLoading(true)
    setErrorMessage("")
    api.auth.requestLoginEmail(
      email,
      ({ securityPhrase: newSecurityPhrase, exchangeToken: newExchangeToken }) => {
        onUpdateLoading(false)
        setSecurityPhrase(newSecurityPhrase)
        setExchangeToken(newExchangeToken)
      },
      (err) => {
        onUpdateLoading(false)
        setErrorMessage(err.message)
      },
    )
  }, [email, onUpdateLoading])

  if (!organisation) {
    return <OrganisationChooser />
  }

  if (organisation?.isBlocked) {
    return (
      <div style={{ maxWidth: 500, display: "block", margin: "0px auto" }}>
        <Message error>
          <p>
            The {organisation.name} licence has now expired. You are no longer able to login and access your account. If
            you have any problems or questions, please contact your organisational administrator.
          </p>
          <p>
            If you don't know who your organisational administrator is then you can request this by emailing{" "}
            <a href="mailto:support@simplydo.co.uk">support@simplydo.co.uk</a>.
          </p>
        </Message>
      </div>
    )
  }

  if (securityPhrase) {
    return (
      <div style={{ textAlign: "center", marginTop: 30 }}>
        <Loader active inline="centered" />
        <h3>Waiting for email authorisation...</h3>
        <p>
          We've sent you an email with a link to authorise this login. If you haven't received it, please check your
          spam folder.
        </p>
        <p>
          <strong>Please do not close this window or refresh the page.</strong>
        </p>

        <Divider />
        <h4>The email should contain this security phrase:</h4>
        <div
          style={{
            marginTop: 5,
            padding: 20,
            background: "rgb(230,230,230)",
            borderRadius: 10,
            textAlign: "center",
          }}
        >
          <strong>{securityPhrase}</strong>
        </div>
      </div>
    )
  }

  if (shouldAutoLogin) {
    return (
      <>
        <Header as="h2">Redirecting to login...</Header>
        <Divider />
        <Loader active inline="centered" />
        <div style={{ height: 5 }} />
      </>
    )
  }

  return (
    <div>
      <Image size="small" src={organisation?.darkLogoUrl} alt="" style={{ margin: "0 auto" }} />
      <Header as="h2">
        {organisation?.appName || organisation?.name
          ? t("entry.login.welcome", { orgName: organisation?.appName || organisation?.name })
          : `Welcome to ${util.appName()}`}
        {organisation?.welcomeText ? (
          <Header.Subheader style={{ whiteSpace: "pre-wrap" }}>{organisation.welcomeText}</Header.Subheader>
        ) : null}
      </Header>
      {paramMessage && paramMessage === "ssoProtectionLoginRequired" ? (
        <Message info>
          We detected that you are trying to access {util.appName()} using an email you had already used before. Please
          log in with your existing account details.
        </Message>
      ) : null}
      {authErrorMessage ? (
        <Message info>We need to verify your details to confirm it's you. Please log in again.</Message>
      ) : null}
      {!util.localStorageIsSupported() ? (
        <Message warning>
          Your device does not support saving data locally. Your login information will not be saved when revisiting{" "}
          {util.appName()}.
        </Message>
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {authIsEnabled("saml") && (
          <Button
            size="small"
            fluid
            primary
            icon="key"
            content={organisation.auth?.saml?.buttonText ?? t("entry.login.sso")}
            onClick={() => util.loginWithSaml(organisation.code)}
          />
        )}
        {authIsEnabled("linkedin") && (
          <Button
            size="small"
            fluid
            color="linkedin"
            icon="linkedin"
            content="Sign in with LinkedIn"
            onClick={() => util.loginWithOAuth("linkedin", organisation.code)}
          />
        )}
        {authIsEnabled("google") && (
          <Button
            size="small"
            fluid
            color="google plus"
            icon="google"
            content="Sign in with Google"
            onClick={() => util.loginWithOAuth("google", organisation.code)}
          />
        )}
        {authIsEnabled("apple") && (
          <Button
            size="small"
            fluid
            color="black"
            icon="apple"
            content="Sign in with Apple"
            onClick={() => util.loginWithOAuth("apple", organisation.code)}
          />
        )}
        {authIsEnabled("microsoft") && (
          <Button
            size="small"
            fluid
            color="msteams"
            icon="microsoft"
            content={organisation.auth?.microsoft?.buttonText ?? "Sign in with Microsoft"}
            onClick={() => util.loginWithOAuth("microsoft", organisation.code)}
          />
        )}
      </div>

      {authIsEnabled("standard") && (
        <>
          {ssoIsEnabled() && <Divider horizontal>OR</Divider>}
          <h5 style={{ marginTop: 0, marginBottom: 5 }}>Enter your email address to continue</h5>
          {errorMessage && !loading && (
            <Message size="small" warning>
              <Icon name="warning circle" />
              {errorMessage}
            </Message>
          )}
          <Form style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {proceedToLogin && otherOrgs.length ? (
              <Message info>
                You already have an account with{" "}
                {otherOrgs.length > 1
                  ? `the following organisations: ${otherOrgs.map((o) => o.name).join(", ")}`
                  : otherOrgs[0].name}
                . You can use your existing account details to login to {organisation.name}.
              </Message>
            ) : null}

            <Input
              fluid
              tabIndex="0"
              value={email}
              icon="mail"
              onChange={(e) => {
                onUpdateEmail(e.target.value)
                setProceedToLogin(false)
                setShowPasswordEntry(false)
                setShowCustomChallenge(false)
                onUpdateChallengeResponse("")
                onUpdatePassword("")
              }}
              onKeyDown={(e) => {
                if (e.keyCode === 13) {
                  if (proceedToLogin) {
                    login()
                  } else {
                    checkAccount()
                  }
                }
              }}
              name="email"
              type="email"
              autoComplete="email"
              placeholder={t("placeholder.email")}
            />
            {showCustomChallenge && (
              <Input
                autoFocus
                fluid
                icon="question"
                label={organisation?.registrationChallenge?.question}
                value={challengeResponse}
                onChange={(e) => onUpdateChallengeResponse(e.target.value)}
              />
            )}
            {organisation.loginLinkEnabled && proceedToLogin ? (
              <Button tabIndex="0" onClick={loginMagicLink} primary fluid>
                Send magic login link
              </Button>
            ) : null}
            {!organisation.loginLinkEnabled ? (
              <>
                <div
                  style={{
                    visibility: showPasswordEntry && proceedToLogin ? "visible" : "hidden",
                    maxHeight: showPasswordEntry && proceedToLogin ? "unset" : "0px",
                  }}
                >
                  <Form.Input
                    tabIndex="0"
                    defaultValue={password}
                    onChange={(e) => onUpdatePassword(e.target.value)}
                    fluid
                    icon="lock"
                    ref={passwordRef}
                    name="password"
                    type="password"
                    placeholder={t("entry.login.yourPass")}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        login()
                      }
                    }}
                  />
                </div>
                {proceedToLogin ? (
                  <Form.Button tabIndex="0" fluid loading={loading} primary onClick={login} name="login-button">
                    {t("entry.login.action")}
                  </Form.Button>
                ) : null}
              </>
            ) : null}
            {!proceedToLogin ? (
              <Form.Button
                tabIndex="0"
                fluid
                loading={loading}
                primary
                onClick={() => checkAccount()}
                name="continue-button"
              >
                {t("generic.continue")}
              </Form.Button>
            ) : null}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: -8,
              }}
            >
              <Link
                style={{
                  cursor: "pointer",
                  margin: 0,
                }}
                onClick={(e) => {
                  e.preventDefault()
                  onSelectOrganisation(null)
                }}
              >
                Not your organisation?
              </Link>
              {proceedToLogin && (
                <Link tabIndex={0} to="/password/forgotten">
                  {t("entry.forgottenPassword.title")}
                </Link>
              )}
            </div>
          </Form>
        </>
      )}

      <Modal mountNode={document.getElementById("semantic-modal-mount-node")} open={otpModal}>
        <Modal.Header>
          <Icon name="lock" style={{ paddingRight: "5px" }} />
          Multi-factor authentication
          <br />
        </Modal.Header>
        <ModalContent>
          <p>
            To complete your login, please enter the six digit one-time password generated by your authentication app.
          </p>
          <div style={{ textAlign: "center" }}>
            <Input
              onKeyDown={(e) => {
                e.keyCode === 13 && login()
              }}
              onChange={(e) => setOtp(e.target.value)}
              value={otp}
              placeholder="123456"
              size="large"
              autoFocus
            />
          </div>
        </ModalContent>
        <ModalActions>
          <Button
            content={t("generic.cancel")}
            onClick={() => {
              setOtpModal(false)
              onUpdateLoading(false)
              setOtp("")
            }}
          />
          <Button primary content={t("generic.continue")} loading={loading} onClick={login} />
        </ModalActions>
      </Modal>
    </div>
  )
}

export default Login
